import { api } from "@/lib/api/client"
import type { BaseResponse, IdOnly } from "@/types/api"

import type {
  CouplagePlanPayload,
  CouplagePlanResult,
  FabricateLotPlannedPayload,
  FabricateLotPlannedResult,
  NextLotNumberResult,
  PatchUsinageLoadsPayload,
  ScheduleUsinagePayload,
  UpdateUsinageWastePayload,
  UsinageDetail,
  UsinageListItem,
} from "./types"

const BASE = "/exporter/usinages"

export type UsinageListFilter = "ACTIVE" | "HISTORY" | "all"

export async function listUsinages(
  status: UsinageListFilter = "all",
): Promise<UsinageListItem[]> {
  const { data } = await api.get<BaseResponse<UsinageListItem[]>>(BASE, {
    params: { status },
  })
  if (!data.success) {
    throw new Error(data.message ?? "Could not load usinages")
  }
  return data.result ?? []
}

export async function getUsinage(
  usinageId: string | number,
): Promise<UsinageDetail> {
  const { data } = await api.get<BaseResponse<UsinageDetail>>(
    `${BASE}/${usinageId}`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not load usinage")
  }
  return data.result
}

export async function scheduleUsinage(
  payload: ScheduleUsinagePayload,
): Promise<IdOnly> {
  const { data } = await api.post<BaseResponse<IdOnly>>(BASE, payload)
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not schedule usinage")
  }
  return data.result
}

export async function startUsinage(
  usinageId: string | number,
): Promise<IdOnly> {
  const { data } = await api.post<BaseResponse<IdOnly>>(
    `${BASE}/${usinageId}/start`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not start usinage")
  }
  return data.result
}

export async function patchUsinageLoads(
  usinageId: string | number,
  payload: PatchUsinageLoadsPayload,
): Promise<IdOnly> {
  const { data } = await api.patch<BaseResponse<IdOnly>>(
    `${BASE}/${usinageId}/loads`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not update usinage loads")
  }
  return data.result
}

// Step 1 — persists the operator's Lot Planned matrix on the usinage
// row and transitions to COUPLAGE_PLANNED. No lots are created here;
// see `fabricateLotPlanned`.
export async function submitCouplage(
  usinageId: string | number,
  payload: CouplagePlanPayload,
): Promise<CouplagePlanResult> {
  const { data } = await api.post<BaseResponse<CouplagePlanResult>>(
    `${BASE}/${usinageId}/couplage`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not submit couplage")
  }
  return data.result
}

// Draft variant of Step 1. Writes the same Lot Planned matrix onto
// the usinage row but keeps the status unchanged (operator can park
// in-progress work and resume later). Backend skips the strict
// validation (empty plan + empty columns allowed) so we keep the
// shape identical to submitCouplage.
export async function saveCouplageDraft(
  usinageId: string | number,
  payload: CouplagePlanPayload,
): Promise<CouplagePlanResult> {
  const { data } = await api.post<BaseResponse<CouplagePlanResult>>(
    `${BASE}/${usinageId}/couplage/draft`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not save draft")
  }
  return data.result
}

// Move a COUPLAGE_PLANNED usinage back to RUNNING so the operator can
// edit the plan. Backend rejects this once any Lot Planned has been
// fabricated.
export async function rewindCouplage(
  usinageId: string | number,
): Promise<void> {
  const { data } = await api.post<BaseResponse<unknown>>(
    `${BASE}/${usinageId}/couplage/rewind`,
  )
  if (!data.success) {
    throw new Error(data.message ?? "Could not rewind couplage")
  }
}

// Step 2 — consumes one planned entry and produces a real lot. The
// classification (LOT vs Queue de Lot) is computed server-side from
// the measured bag count and net weight.
export async function fabricateLotPlanned(
  usinageId: string | number,
  lotPlannedId: string,
  payload: FabricateLotPlannedPayload,
): Promise<FabricateLotPlannedResult> {
  const { data } = await api.post<BaseResponse<FabricateLotPlannedResult>>(
    `${BASE}/${usinageId}/lots-planned/${lotPlannedId}/fabricate`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not fabricate lot")
  }
  return data.result
}

// Pre-fill helper for the fabrication dialog. Returns the next
// canonical `LOT-YYYY-####` for this exporter; the operator may
// override with any non-empty string before submitting.
export async function suggestNextLotNumber(): Promise<string> {
  const { data } = await api.get<BaseResponse<NextLotNumberResult>>(
    `/exporter/lots/next-number`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not load next lot number")
  }
  return data.result.lotId
}

// Records the operator-reported milling waste (déchet) figure on the
// usinage row. Allowed only once the run has produced output
// (FABRICATION or COMPLETED) — earlier statuses 422 server-side.
export async function updateUsinageWaste(
  usinageId: string | number,
  payload: UpdateUsinageWastePayload,
): Promise<void> {
  const { data } = await api.patch<BaseResponse<unknown>>(
    `${BASE}/${usinageId}/waste`,
    payload,
  )
  if (!data.success) {
    throw new Error(data.message ?? "Could not update waste")
  }
}

export async function deleteUsinage(
  usinageId: string | number,
): Promise<IdOnly> {
  const { data } = await api.delete<BaseResponse<IdOnly>>(
    `${BASE}/${usinageId}`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not delete usinage")
  }
  return data.result
}
